'use client'

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode
} from 'react'
import type { RefObject } from 'react'

import type { AxiosInstance } from 'axios'
import { getProviders, getSession, signIn, signOut } from 'next-auth/react'
import type { signIn as NextAuthSignIn } from 'next-auth/react'
import type ReCAPTCHA from 'react-google-recaptcha'
import Swal from 'sweetalert2'

import { useAuthCookies } from '../hooks/useAuthCookies'

import { env } from '@/env'

import { useAppRouter, useAppSearchParams } from '@/shared/lib/router'
import { showErrorAlert } from '@/shared/lib/ui'



// ---- Local auth types ----

export type AuthAction =
  | 'sign-in'
  | 'sign-up'
  | 'forgot-password'
  | 'reset-password'
  | 'confirmation'
  | 'resend-confirmation'

export interface AsyncConfig {
  reCaptchaKey?: string
  disableReCaptcha?: boolean | AuthAction[]
  reCaptchaVersion?: 'v2' | 'v3'
}

export interface AuthRoutesConfig {
  signIn?: string
  signUp?: string
  forgotPassword?: string
  resetPassword?: string
  confirmation?: string
  resendConfirmation?: string
}

export interface AuthConfig extends AsyncConfig {
  afterSignInRedirectUrl?: string
  customNavigation?: boolean
  httpClient: AxiosInstance
  initialAction?: AuthAction
  checkSessionOnAuthPages?: boolean
  enabledProviders?: string[]
  callbackUrl?: string
  authRoutes?: AuthRoutesConfig
  signIn?: {
    loginLabel?: string
    hideSignUpAction?: boolean
    hideForgotPasswordAction?: boolean
  }
  signUp?: {
    withEmailConfirmation?: boolean
    skipConfirmation?: boolean
    hideSignInAction?: boolean
  }
}

export interface AuthContextValue {
  action: AuthAction
  providers: Record<string, any>

  currentEmail?: string
  currentPassword?: string

  reCaptchaEnabled: boolean
  useReCaptchaV2: boolean
  useReCaptchaV3: boolean
  reCaptchaRef?: RefObject<ReCAPTCHA>
  httpClient: AxiosInstance

  callbackUrl: string

  config: AuthConfig

  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void

  goToSignIn(): void
  goToSignUp(): void
  goToForgotPassword(opts?: { email?: string }): void
  goToResetPassword(opts?: { email?: string }): void
  goToConfirmation(opts: { email: string; password?: string }): void
  goToResendConfirmation(): void
  backAction(): void
  setAction(next: AuthAction): void

  handleSignIn(data: {
    login: string
    password: string
  }): Promise<Awaited<ReturnType<typeof NextAuthSignIn>>>
  onRequestPasswordReset(data: { email: string }): Promise<void>
  onRequestConfirmationToken(data: { email: string }): Promise<void>

  setCurrentEmail(email: string): void
  setCurrentPassword(password: string): void

  handleLogout(): void
}

// ---- Provider ----

export interface AuthProviderProps {
  config: AuthConfig
  children: ReactNode
}

const AuthContext = createContext<AuthContextValue | null>(null)

export const useAuthContext = (): AuthContextValue => {
  const context = useContext(AuthContext)
  if (!context)
    throw new Error('useAuthContext must be used within AuthProvider')
  return context
}

export const AuthProvider = ({ config, children }: AuthProviderProps) => {
  const {
    afterSignInRedirectUrl = '/',
    customNavigation = false,
    httpClient,
    initialAction = 'sign-in',
    checkSessionOnAuthPages = false,
    disableReCaptcha = false,
    reCaptchaVersion = 'v3',
    reCaptchaKey = env.NEXT_PUBLIC_PGEE_RECAPTCHA_SITE_KEY,
    enabledProviders = [],
    authRoutes,
    callbackUrl: propsCallbackUrl
  } = config
  const defaultAuthRoutes = {
    signIn: '/sign-in',
    signUp: '/sign-up',
    forgotPassword: '/forgot-password',
    resetPassword: '/reset-password',
    confirmation: '/confirmation',
    resendConfirmation: '/resend-confirmation'
  }
  const getAuthRoute = (route: keyof typeof defaultAuthRoutes) => {
    return authRoutes?.[route] ?? defaultAuthRoutes[route]
  }

  const router = useAppRouter()
  const searchParams = useAppSearchParams()
  const cookies = useAuthCookies()

  const callbackUrl =
    propsCallbackUrl ??
    searchParams.get('callbackUrl') ??
    afterSignInRedirectUrl

  const [action, setAction] = useState<AuthAction>(initialAction)
  const [lastAction, setLastAction] = useState<AuthAction | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [providers, setProviders] = useState<Record<string, any>>({})

  const reCaptchaEnabled =
    !!reCaptchaKey &&
    !(Array.isArray(disableReCaptcha)
      ? disableReCaptcha.includes(action)
      : disableReCaptcha)

  const useReCaptchaV3 = reCaptchaEnabled && reCaptchaVersion === 'v3'
  const useReCaptchaV2 = reCaptchaEnabled && reCaptchaVersion === 'v2'

  const navigate = useCallback(
    (path: string) => {
      if (!customNavigation) router.push(path)
    },
    [router, customNavigation]
  )

  const goToSignIn = useCallback(() => {
    setLastAction(action)
    setAction('sign-in')
    navigate(getAuthRoute('signIn'))
  }, [action, navigate])

  const goToSignUp = useCallback(() => {
    setLastAction(action)
    setAction('sign-up')
    navigate(getAuthRoute('signUp'))
  }, [action, navigate])

  const goToForgotPassword = useCallback(
    (opts?: { email?: string }) => {
      if (opts?.email) cookies.setEmail(opts.email)
      setLastAction(action)
      setAction('forgot-password')
      navigate(getAuthRoute('forgotPassword'))
    },
    [action, navigate, cookies]
  )

  const goToResetPassword = useCallback(
    (opts?: { email?: string }) => {
      if (opts?.email) cookies.setEmail(opts.email)
      setLastAction(action)
      setAction('reset-password')
      navigate(getAuthRoute('resetPassword'))
    },
    [action, navigate, cookies]
  )

  const goToConfirmation = useCallback(
    (opts: { email: string; password?: string }) => {
      cookies.setEmail(opts.email)
      if (opts.password) cookies.setPassword(opts.password)
      setLastAction(action)
      setAction('confirmation')
      navigate(
        `${getAuthRoute('confirmation')}?email=${encodeURIComponent(
          opts.email
        )}`
      )
    },
    [action, navigate, cookies]
  )

  const goToResendConfirmation = useCallback(() => {
    setLastAction(action)
    setAction('resend-confirmation')
    navigate(getAuthRoute('resendConfirmation'))
  }, [action, navigate])

  const backAction = useCallback(() => {
    if (lastAction) setAction(lastAction)
    setLastAction(null)
  }, [lastAction])

  const handleSignIn = useCallback(
    async (data: { login: string; password: string }) => {
      setIsLoading(true)
      const result = await signIn('domain-sign-in', {
        login: data.login,
        password: data.password,
        callbackUrl,
        redirect: false
      })
      setIsLoading(false)
      return result
    },
    [callbackUrl]
  )

  const onRequestPasswordReset = useCallback(
    async (data: { email: string }) => {
      setIsLoading(true)
      try {
        await httpClient.post('/auth/reset-password', { email: data.email })
        goToResetPassword({ email: data.email })
      } catch (err) {
        showErrorAlert(err)
      } finally {
        setIsLoading(false)
      }
    },
    [httpClient, goToResetPassword]
  )

  const onRequestConfirmationToken = useCallback(
    async (data: { email: string }) => {
      setIsLoading(true)
      try {
        await httpClient.post('/auth/request-confirmation-token', {
          email: data.email
        })
        goToConfirmation({ email: data.email })
      } catch (err) {
        showErrorAlert(err)
      } finally {
        setIsLoading(false)
      }
    },
    [httpClient, goToConfirmation]
  )

  useEffect(() => {
    if (!checkSessionOnAuthPages) return
    getSession().then(session => {
      if (session) {
        Swal.fire({
          icon: 'success',
          title: 'Sessão ativa',
          text: 'Você já está logado. Deseja continuar?',
          showDenyButton: true,
          confirmButtonText: 'Sim',
          denyButtonText: 'Sair'
        }).then(res =>
          res.isDenied ? signOut() : router.push(afterSignInRedirectUrl)
        )
      }
    })
  }, [checkSessionOnAuthPages, afterSignInRedirectUrl, router])

  useEffect(() => {
    const error = searchParams.get('error')
    if (error) {
      Swal.fire({ icon: 'error', title: 'Erro', text: error })
    }
  }, [searchParams])

  useEffect(() => {
    if (!enabledProviders.length) return
    getProviders().then(all => {
      const filtered: Record<string, any> = {}
      enabledProviders.forEach(key => {
        if (all?.[key]) filtered[key] = all[key]
      })
      setProviders(filtered)
    })
  }, [enabledProviders])

  const handleLogout = useCallback(() => {
    cookies.clearAll()
    signOut()
  }, [cookies])

  const contextValue: AuthContextValue = {
    httpClient,
    action,
    providers,
    isLoading,
    setIsLoading,
    currentEmail: cookies.getEmail(),
    currentPassword: cookies.getPassword(),
    reCaptchaEnabled,
    useReCaptchaV3,
    useReCaptchaV2,
    callbackUrl,
    goToSignIn,
    goToSignUp,
    goToForgotPassword,
    goToResetPassword,
    goToConfirmation,
    goToResendConfirmation,
    backAction,
    setAction,
    handleSignIn,
    onRequestPasswordReset,
    onRequestConfirmationToken,
    setCurrentEmail: cookies.setEmail,
    setCurrentPassword: cookies.setPassword,
    handleLogout,
    config
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}
